import { useMemo } from 'react';

import { ListingType } from 'algolia-api/types/ListingType';
import { SearchLocation } from 'algolia-api/types/SearchLocation';
import { StaffTypeFilter } from 'algolia-api/types/StaffTypeFilter';
import { Box } from 'components/Box';
import { ListingTypeSelect } from 'components/Listing/ListingTypeSelect';
import { LocationInput } from 'components/LocationInput/LocationInput';
import { SearchButton } from 'components/Search/SearchButton';
import { TabContext } from 'containers/TabContext';
import { UncontrolledSearchQueryInputContainer } from 'containers/UncontrolledSearchQueryInputContainer';

import { StaffPageHeaderSearchDesktopInputsForm } from './StaffPageHeaderSearchInputs.styled';

type DisplayedType = ListingType | StaffTypeFilter;

type Props = {
  selectedType: DisplayedType;
  listingTypes: Array<DisplayedType>;
  onSearch?: () => void;
  query: string;
  initialQuery: string;
  searchLocation: SearchLocation | null | undefined;
  searchLocationDisabled?: boolean;
  setListingType: (arg0: ListingType) => void;
  setQuery: (arg0: string) => void;
  onLocationChange: (searchLocation: SearchLocation | null | undefined) => void;
  search: (arg0: string) => void;
  clearSearchInput: () => void;
  searchInputId: string;
  searchInputQaId: string;
};

export function StaffPageHeaderSearchInputs({
  selectedType,
  listingTypes,
  onSearch,
  query,
  initialQuery,
  searchLocation,
  searchLocationDisabled,
  setListingType,
  setQuery,
  onLocationChange,
  search,
  clearSearchInput,
  searchInputId,
  searchInputQaId,
}: Props) {
  const locationInput = useMemo(
    () => (
      <LocationInput
        affixVariant="contrast"
        styleVariant="contrast"
        disabled={searchLocationDisabled}
        hasRemoteOptions
        showLocationIcon
        showClearButton
        placeholder={getText('Everywhere')}
        locationText={searchLocation?.text}
        onChange={onLocationChange}
      />
    ),
    [searchLocation, searchLocationDisabled, onLocationChange],
  );
  return (
    <TabContext.Consumer>
      {(isTabbing) => (
        <StaffPageHeaderSearchDesktopInputsForm
          onSubmit={(e) => {
            e.preventDefault();
            search(query);

            onSearch?.();
          }}
          data-qa-id="search-inputs"
          $isTabbing={isTabbing}
        >
          <Box component="label" display="block" flex="1">
            <Box display="none">{getText('Select listing type')}</Box>
            <ListingTypeSelect
              value={selectedType}
              onChange={setListingType}
              listingTypes={listingTypes}
              variant="grey"
            />
          </Box>

          <Box height="100%" width="100%" flex={[null, null, null, '2']}>
            <UncontrolledSearchQueryInputContainer
              initialQuery={initialQuery}
              placeholder={getText('Search by keyword, skill, or interest')}
              xsPlaceholder={getText('Keyword, skill, or interest')}
              onChange={setQuery}
              onClearQuery={clearSearchInput}
              presentation="contrast"
              id={searchInputId}
              qaId={searchInputQaId}
            />
          </Box>

          <Box flex="1">{locationInput}</Box>

          <Box
            flex="0 0 auto"
            height="100%"
            display={['block', null, null, 'flex']}
          >
            <SearchButton />
          </Box>
        </StaffPageHeaderSearchDesktopInputsForm>
      )}
    </TabContext.Consumer>
  );
}
